import { useCallback } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router-dom';
import { useNavPrefs } from './nav-prefs-context';
import { matchNavTarget } from './nav-prefs-match';

type To = string | number;

/** Drop-in replacement for `useNavigate()` that opens the destination in a
 *  new browser tab when the user enabled the "open in new tab" preference
 *  for the matched (entryId, kind). Numeric back/forward navigation
 *  (`navigate(-1)`) bypasses the check. */
export function useSmartNavigate() {
  const navigate = useNavigate();
  const { shouldOpenInNewTab } = useNavPrefs();

  return useCallback((to: To, options?: NavigateOptions) => {
    if (typeof to === 'number') {
      navigate(to);
      return;
    }
    const match = matchNavTarget(to);
    if (match && shouldOpenInNewTab(match.entry.id, match.kind)) {
      window.open(to, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(to, options);
  }, [navigate, shouldOpenInNewTab]);
}
